import { useState, useEffect, useCallback } from 'react';
import { withTheme } from '@emotion/react';
import PropTypes from 'prop-types';

const MatchContainer = ({ minWidth, maxWidth, queries, theme, children }) => {
    const [matches, setMatches] = useState({});

    const getMediaQuery = useCallback((query, key, breakpoints) => {
        const value = breakpoints[key];
        if (typeof value === 'string') {
            return value;
        }
        return `(${query}: ${value}px)`;
    }, []);

    const handleMatchChange = useCallback(
        (key) => (l) => {
            setMatches((prevMatches) => ({
                ...prevMatches,
                [key]: l.matches,
            }));
        },
        []
    );

    const setMatcher = useCallback(
        (key, mediaQuery) => {
            const matcher = window.matchMedia(mediaQuery);
            const fn = handleMatchChange(key);
            matcher.addListener(fn);
            fn(matcher);
            return matcher;
        },
        [handleMatchChange]
    );

    const setMatchers = useCallback(() => {
        const matchers = {};

        if (queries && queries.length) {
            queries.forEach((key) => {
                const mediaQuery = getMediaQuery('min-width', key, theme.breakpoint);
                matchers[key] = setMatcher(key, mediaQuery);
            });
        }

        if (minWidth) {
            const mediaQuery = getMediaQuery('min-width', minWidth, theme.breakpoint);
            matchers.minWidth = setMatcher('minWidth', mediaQuery);
        }

        if (maxWidth) {
            const mediaQuery = getMediaQuery('max-width', maxWidth, theme.breakpoint);
            matchers.maxWidth = setMatcher('maxWidth', mediaQuery);
        }

        return matchers;
    }, [getMediaQuery, setMatcher, theme.breakpoint, queries, minWidth, maxWidth]);

    useEffect(() => {
        const matchers = setMatchers();

        return () => {
            Object.keys(matchers).forEach((key) => {
                matchers[key].removeListener(handleMatchChange(key));
            });
        };
    }, [setMatchers, handleMatchChange]);

    if (typeof children === 'function') {
        return children(matches);
    }

    const matched = Object.values(matches).filter((v) => v);

    return matched.length ? children : null;
};

export const MediaMatcher = withTheme(MatchContainer);

MediaMatcher.propTypes = {
    theme: PropTypes.object,
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    maxWidth: PropTypes.string,
    minWidth: PropTypes.string,
};
